import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
// Static imports removed for async loading
import SEO from '../components/SEO';
import Footer from '../components/layout/Footer';
import Header from '../components/layout/Header';
import WorldMap from '../components/WorldMap';
import CountryFilter from '../components/CountryFilter';
import CountryComparator from '../components/CountryComparator';
import { worldMapPaths } from '../components/WorldMapData';
import HelpTooltip from '../components/HelpTooltip';
import {
    getSystemTypeLabel,
    getPressureValveLabel,
    getTraitLabel,
    getSystemTypeHelp,
    getPressureValveHelp,
    getTraitHelp,
    PRESSURE_VALVE_COLORS
} from '../config/labelConfig';
import { parseMarkdownItalics } from '../components/SafecareBrand';
import './premium-article.css';

// Colors and labels now imported from labelConfig

function ShockTherapyArticle() {
    const { articleId } = useParams();
    const [article, setArticle] = useState(null);
    const { scrollY } = useScroll();
    const [expandedCountryCodes, setExpandedCountryCodes] = useState([]);

    // State
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [contentData, setContentData] = useState(null);
    const [extensionData, setExtensionData] = useState(null);
    const [mapVisualizationMode, setMapVisualizationMode] = useState('pressure_valve');
    const [filteredCountries, setFilteredCountries] = useState(null);
    const [allCountries, setAllCountries] = useState([]);

    // Derived selected country
    const selectedCountry = article?.countries?.find(c => c.code.toLowerCase() === articleId?.toLowerCase());

    // Parallax for Hero
    const yHero = useTransform(scrollY, [0, 500], [0, 200]);
    const opacityHero = useTransform(scrollY, [0, 300], [1, 0]);

    // Merge Data and Sync filteredCountries when article loads or extension data arrives
    useEffect(() => {
        if (article?.countries && extensionData) {
            // Merge logic: Start with main content, append extension items that don't exist in main
            const mainCodes = new Set(article.countries.map(c => c.code.toLowerCase()));
            const uniqueExtension = (extensionData.countries || []).filter(c => c.code && !mainCodes.has(c.code.toLowerCase()));

            // Combine
            const combined = [...article.countries, ...uniqueExtension];
            setAllCountries(combined);
            setFilteredCountries(combined);
        } else if (article?.countries) {
            // Fallback if extension not yet loaded or doesn't exist
            setAllCountries(article.countries);
            setFilteredCountries(article.countries);
        }
    }, [article, extensionData]);

    // Fetch Data on Mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                // Add timestamp to prevent caching issues during dev
                const timestamp = Date.now();
                // Load Index and Extension (Index is small, used for context if needed)
                const [indexRes, extensionRes] = await Promise.all([
                    fetch(`/data/shock_therapy_index.json?v=${timestamp}`),
                    fetch(`/data/shock_therapy_countries_extension.json?v=${timestamp}`)
                ]);

                if (!indexRes.ok) throw new Error(`Index load failed: ${indexRes.status}`);
                if (!extensionRes.ok) throw new Error(`Extension load failed: ${extensionRes.status}`);

                const index = await indexRes.json();
                const extension = await extensionRes.json();

                setContentData(index);
                setExtensionData(extension);
                // setLoading stays true until article specifically loads
            } catch (error) {
                console.error("Failed to load shock therapy index:", error);
                setError(error.message);
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Fetch Specific Article when ID changes
    useEffect(() => {
        const fetchArticle = async () => {
            if (!articleId) return;
            // Always set loading true when switching articles
            setLoading(true);
            try {
                const timestamp = Date.now();
                const res = await fetch(`/data/shock-therapy/articles/${articleId}.json?v=${timestamp}`);
                if (!res.ok) throw new Error(`Article load failed: ${res.status}`);
                const data = await res.json();
                setArticle(data);
                setLoading(false);
                window.scrollTo(0, 0);
            } catch (error) {
                console.error("Failed to load article:", error);
                setError(`Article "${articleId}" not found or failed to load.`);
                setLoading(false);
            }
        };
        fetchArticle();
    }, [articleId]);



    if (loading) {
        return (
            <div className="shock-article-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: 'var(--color-off-white)', color: 'var(--text-primary)' }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⚡</div>
                    <div style={{ fontWeight: 600 }}>Loading Data...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="shock-article-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: 'var(--color-off-white)' }}>
                <div style={{ textAlign: 'center', maxWidth: '600px', padding: '2rem' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
                    <h2 style={{ marginBottom: '1rem', color: 'var(--color-blue-dark)' }}>Failed to Load Content</h2>
                    <p style={{ color: 'var(--text-muted)', marginBottom: '2rem' }}>{error}</p>
                    <Link to="/shock-therapy" style={{ padding: '0.8rem 1.6rem', background: 'var(--color-blue)', color: '#fff', borderRadius: '8px', textDecoration: 'none', fontWeight: 700 }}>
                        Return to Hub
                    </Link>
                </div>
            </div>
        );
    }

    if (!article) {
        return (
            <div className="shock-article-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: 'var(--color-off-white)' }}>
                <div style={{ textAlign: 'center', maxWidth: '600px', padding: '2rem' }}>
                    <h2 style={{ marginBottom: '1rem', color: 'var(--color-blue-dark)' }}>Article Not Found</h2>
                    <p style={{ color: 'var(--text-muted)', marginBottom: '2rem' }}>We couldn't find the article "{articleId}".</p>
                    <Link to="/shock-therapy" style={{ padding: '0.8rem 1.6rem', background: 'var(--color-blue)', color: '#fff', borderRadius: '8px', textDecoration: 'none', fontWeight: 700 }}>
                        Return to Hub
                    </Link>
                </div>
            </div>
        );
    }

    // Stylize Branding Helper
    const stylizeBranding = (text) => {
        if (typeof text !== 'string') return text;
        return text
            .replace(/SAFEC\+\+/g, 'SAFE<em style="font-style: italic; font-weight: bold; text-transform: none;">C</em><span style="color: #ff0000; font-weight: 800;">+</span>')
            .replace(/SAFEC\+/g, 'SAFE<em style="font-style: italic; font-weight: bold; text-transform: none;">C</em><span style="color: #ff0000; font-weight: 800;">+</span>');
    };

    // Rich Text Renderer Wrapper
    const RichTextRenderer = ({ content }) => (
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
                p: ({ node, ...props }) => <p {...props} />,
                ul: ({ node, ...props }) => <ul style={{ marginBottom: '2rem', paddingLeft: '1.5rem' }} {...props} />,
                li: ({ node, ...props }) => <li style={{ marginBottom: '0.8rem' }} {...props} />,
                a: ({ node, ...props }) => <a style={{ color: '#2997ff', textDecoration: 'none' }} {...props} />
            }}
        >
            {stylizeBranding(content)}
        </ReactMarkdown>
    );

    return (
        <div className="premium-container">
            <SEO
                title={`${article.title} | SAFECARE`}
                description={article.summary}
                image={article.image || '/image/og-default.jpg'}
            />

            <Header />

            {/* --- HERO SECTION --- */}
            <motion.div className="premium-hero" style={{ opacity: opacityHero }}>
                <motion.div className="premium-hero-bg">
                    {/* Background Image Layer - Blurred */}
                    {article.image && <img src={article.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                </motion.div>

                <motion.div
                    className="premium-hero-content"
                    style={{ y: yHero }}
                >
                    <motion.span
                        className="premium-eyebrow"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        Shock Therapy Series
                    </motion.span>
                    <motion.h1
                        className="premium-title"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, duration: 0.8 }}
                    >
                        {parseMarkdownItalics(article.title)}
                    </motion.h1>
                    <motion.p
                        className="premium-subtitle"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.6 }}
                    >
                        {parseMarkdownItalics(article.summary)}
                    </motion.p>
                </motion.div>
            </motion.div>

            {/* --- CONTENT SECTION --- */}
            <div className="premium-content-wrapper">

                {/* Unified Glass Panel for ALL content */}
                <motion.article
                    className="premium-glass-panel"
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{ duration: 0.8 }}
                >
                    {/* Intro for Internation Comparison Article mainly */}
                    {article.intro && (
                        <div className="premium-prose">
                            <RichTextRenderer content={article.intro} />
                        </div>
                    )}

                    {/* Flag Grid Special Component */}
                    {article.countries && (
                        <>
                            {articleId === 'international-comparison' && (
                                <>
                                    <CountryFilter
                                        countries={allCountries}
                                        onFilterChange={setFilteredCountries}
                                        onMapModeChange={setMapVisualizationMode}
                                        currentMapMode={mapVisualizationMode}
                                    />
                                    <WorldMap
                                        countries={filteredCountries || allCountries}
                                        activeCountryCode={expandedCountryCodes[expandedCountryCodes.length - 1]}
                                        visualizationMode={mapVisualizationMode}
                                        onCountrySelect={(country) => {
                                            if (!country) return;
                                            setExpandedCountryCodes(prev => {
                                                // If already in the list, move it to the end (make it active)
                                                if (prev.includes(country.code)) {
                                                    return [...prev.filter(c => c !== country.code), country.code];
                                                }
                                                // Otherwise, add it to the list
                                                return [...prev, country.code];
                                            });
                                        }}
                                    />
                                </>
                            )}
                            <div className="premium-flag-grid" style={{ marginBottom: '4rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                {/* Only render countries that have been clicked on the map */}
                                {/* Show active country first, then collapsed countries in reverse order */}
                                {(() => {
                                    if (expandedCountryCodes.length === 0) return null;

                                    // Get the active country (last in array)
                                    const activeCode = expandedCountryCodes[expandedCountryCodes.length - 1];
                                    const activeCountry = (filteredCountries || allCountries).find(c => c.code === activeCode);

                                    // Get collapsed countries (all except last) in reverse order
                                    const collapsedCodes = expandedCountryCodes.slice(0, -1).reverse();
                                    const collapsedCountries = collapsedCodes
                                        .map(code => (filteredCountries || allCountries).find(c => c.code === code))
                                        .filter(Boolean);

                                    // Render active first, then collapsed
                                    const orderedCountries = [
                                        { country: activeCountry, isExpanded: true },
                                        ...collapsedCountries.map(country => ({ country, isExpanded: false }))
                                    ];

                                    return orderedCountries.map(({ country, isExpanded }, idx) => {
                                        if (!country) return null;

                                        return (
                                            <motion.div
                                                key={country.code}
                                                id={`country-${country.code}`}
                                                className={`premium-flag-card ${isExpanded ? 'is-expanded' : 'is-collapsed'}`}
                                                initial={{ opacity: 0, scale: 0.95 }}
                                                whileInView={{ opacity: 1, scale: 1 }}
                                                viewport={{ once: true }}
                                                transition={{ delay: idx * 0.05 }}
                                            >
                                                <div className="country-shape-bg">
                                                    <svg viewBox="0 0 1009 650" preserveAspectRatio="xMidYMid meet">
                                                        <path d={worldMapPaths.find(p => p.id === country.code.toUpperCase())?.d} />
                                                    </svg>
                                                </div>

                                                {/* Clickable Header */}
                                                <div
                                                    className="premium-flag-header"
                                                    onClick={() => {
                                                        if (isExpanded) {
                                                            // If clicking the active card, remove it from the list
                                                            setExpandedCountryCodes(prev => prev.filter(c => c !== country.code));
                                                        } else {
                                                            // If clicking a collapsed card, make it active
                                                            setExpandedCountryCodes(prev => [...prev.filter(c => c !== country.code), country.code]);
                                                        }
                                                    }}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', width: '100%' }}>
                                                        {country.code && (
                                                            <img
                                                                src={`/image/flags/${country.code.toLowerCase()}.svg`}
                                                                alt={country.name}
                                                                style={{ width: 48, height: 36, borderRadius: 6, objectFit: 'cover', boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}
                                                            />
                                                        )}
                                                        <h3 style={{ margin: 0, fontSize: '1.4rem', color: 'var(--color-blue-dark)', fontWeight: 800, flexGrow: 1 }}>
                                                            {country.name}
                                                            {/* System DNA Traits Chips (Collapsed View) */}
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                                                                {country.classifiers?.traits?.slice(0, 4).map(trait => (
                                                                    <span key={trait} style={{
                                                                        fontSize: '0.65rem',
                                                                        padding: '2px 8px',
                                                                        borderRadius: '12px',
                                                                        background: 'rgba(148, 163, 184, 0.1)',
                                                                        color: '#94a3b8',
                                                                        border: '1px solid rgba(148, 163, 184, 0.2)',
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center'
                                                                    }}>
                                                                        {getTraitLabel(trait)}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </h3>
                                                        <motion.div
                                                            animate={{ rotate: isExpanded ? 180 : 0 }}
                                                            style={{ color: '#94a3b8' }}
                                                        >
                                                            ▼
                                                        </motion.div>
                                                    </div>
                                                </div>

                                                {/* Collapsible Content */}
                                                <div style={{ overflow: 'hidden' }}>
                                                    <motion.div
                                                        initial={false}
                                                        animate={{ height: isExpanded ? 'auto' : 0, opacity: isExpanded ? 1 : 0 }}
                                                        transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
                                                    >
                                                        <div className="premium-flag-content">
                                                            {/* System Type Badge */}
                                                            {country.classifiers?.system_type && (
                                                                <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                                                    <span style={{ fontSize: '0.8rem', color: '#94a3b8', textTransform: 'uppercase', display: 'flex', alignItems: 'center' }}>
                                                                        System Type
                                                                        <HelpTooltip text={getSystemTypeHelp(country.classifiers.system_type)} position="bottom" />
                                                                        :
                                                                    </span>
                                                                    <span style={{
                                                                        fontSize: '0.8rem',
                                                                        fontWeight: 600,
                                                                        padding: '4px 10px',
                                                                        borderRadius: '6px',
                                                                        background: 'rgba(59, 130, 246, 0.15)',
                                                                        color: '#60a5fa',
                                                                        border: '1px solid rgba(59, 130, 246, 0.3)'
                                                                    }}>
                                                                        {getSystemTypeLabel(country.classifiers.system_type)}
                                                                    </span>
                                                                </div>
                                                            )}

                                                            {/* Pressure Valve Badge (Expanded) */}
                                                            {country.classifiers?.pressure_valve_primary && (
                                                                <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                                                    <span style={{ fontSize: '0.8rem', color: '#94a3b8', textTransform: 'uppercase', display: 'flex', alignItems: 'center' }}>
                                                                        Pressure Valve
                                                                        <HelpTooltip text={getPressureValveHelp(country.classifiers.pressure_valve_primary)} position="bottom" />
                                                                        :
                                                                    </span>
                                                                    <span style={{
                                                                        fontSize: '0.8rem',
                                                                        fontWeight: 600,
                                                                        padding: '4px 10px',
                                                                        borderRadius: '6px',
                                                                        background: `${PRESSURE_VALVE_COLORS[country.classifiers.pressure_valve_primary]}20`,
                                                                        color: PRESSURE_VALVE_COLORS[country.classifiers.pressure_valve_primary],
                                                                        border: `1px solid ${PRESSURE_VALVE_COLORS[country.classifiers.pressure_valve_primary]}40`
                                                                    }}>
                                                                        {getPressureValveLabel(country.classifiers.pressure_valve_primary)}
                                                                    </span>
                                                                </div>
                                                            )}

                                                            <div className="premium-flag-stats">
                                                                {country.attributes.map((attr, i) => {
                                                                    // Helper to determine status class
                                                                    let statusClass = '';
                                                                    const lowerLabel = attr.label.toLowerCase();
                                                                    if (lowerLabel.includes('non-transferable') || lowerLabel.includes('us constraints')) statusClass = 'status-danger';
                                                                    else if (lowerLabel.includes('transferable') || lowerLabel.includes('us viability')) statusClass = 'status-success';

                                                                    return (
                                                                        <div key={i} className={`stat-item ${statusClass}`}>
                                                                            <span className="stat-label">{attr.label}</span>
                                                                            <span className="stat-value">
                                                                                <RichTextRenderer content={attr.value} />
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>

                                                            {country.analysis && (
                                                                <div className="premium-flag-verdict">
                                                                    <span className="verdict-label">The Verdict</span>
                                                                    <div className="verdict-text">
                                                                        <RichTextRenderer content={country.analysis} />
                                                                    </div>
                                                                </div>
                                                            )}

                                                            <div style={{ marginTop: '1.5rem', textAlign: 'right' }}>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        document.querySelector('.world-map-container')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                                    }}
                                                                    style={{
                                                                        background: 'transparent',
                                                                        border: '1px solid #334155',
                                                                        color: '#94a3b8',
                                                                        padding: '0.4rem 0.8rem',
                                                                        borderRadius: '6px',
                                                                        cursor: 'pointer',
                                                                        fontSize: '0.8rem',
                                                                        transition: 'all 0.2s ease'
                                                                    }}
                                                                    onMouseEnter={(e) => { e.target.style.borderColor = '#94a3b8'; e.target.style.color = '#fff'; }}
                                                                    onMouseLeave={(e) => { e.target.style.borderColor = '#334155'; e.target.style.color = '#94a3b8'; }}
                                                                >
                                                                    ↑ Back to Map
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                </div>
                                            </motion.div>
                                        );
                                    });
                                })()}
                            </div>

                            {/* Comparator Section */}
                            {articleId === 'international-comparison' && (
                                <CountryComparator countries={article.countries} />
                            )}
                        </>
                    )}

                    {/* Standard Article Flow */}
                    {(article.scam || article.issue) && (
                        <>
                            <h2 className="premium-section-header trap-color">The Trap</h2>
                            <div className="premium-prose">
                                <RichTextRenderer content={article.issue || article.scam} />
                            </div>
                        </>
                    )}

                    {article.fix && (
                        <>
                            <h2 className="premium-section-header fix-color" style={{ marginTop: '4rem' }}>The Fix</h2>
                            <div className="premium-prose">
                                <RichTextRenderer content={article.fix} />
                            </div>
                        </>
                    )}

                    {/* Criticism Section Refactored As Vertical Debate Blocks */}
                    {article.criticism && (
                        <>
                            <h2 className="premium-section-header" style={{ marginTop: '4rem' }}>The Critics</h2>
                            <div className="premium-debate-stack">
                                {Array.isArray(article.criticism) ? article.criticism.map((item, index) => {
                                    // Split logic: Find "**The Reality:**" or similar pattern
                                    const parts = item.rebuttal.split(/\*\*The Reality:?\*\*|The Reality:/i);
                                    const allegation = parts.length > 1 ? parts[0] : null;
                                    const reality = parts.length > 1 ? parts[1] : parts[0];

                                    return (
                                        <div key={index} className="premium-debate-block">
                                            {/* LAYER 1: The Myth / The Critic */}
                                            <div className="premium-debate-top">
                                                <span className="premium-criticism-badge" style={{
                                                    background: item.type === 'myth' ? '#ff9f0a' : '#bf5af2'
                                                }}>
                                                    {item.type}
                                                </span>
                                                <h3 className="debate-claim">{item.claim}</h3>
                                                {/* If we found an allegation part, show it here as context */}
                                                {allegation && (
                                                    <div className="debate-allegation-quote">
                                                        <RichTextRenderer content={allegation} />
                                                    </div>
                                                )}
                                            </div>

                                            {/* LAYER 2: The Reality */}
                                            <div className="premium-debate-bottom">
                                                <span className="debate-reality-label">The Reality</span>
                                                <div className="premium-prose" style={{ marginBottom: 0, fontSize: '1.2rem' }}>
                                                    <RichTextRenderer content={reality.trim()} />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }) : (
                                    <div className="premium-prose">
                                        <RichTextRenderer content={article.criticism} />
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </motion.article>

                {/* Footer Navigation */}
                <div style={{ marginTop: '4rem', display: 'flex', justifyContent: 'center' }}>
                    <Link to="/shock-therapy" style={{
                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                        padding: '1rem 2rem', background: 'var(--color-blue)', borderRadius: '99px',
                        color: '#fff', textDecoration: 'none', fontWeight: 700,
                        boxShadow: '0 8px 24px rgba(37,99,235,0.3)'
                    }}>
                        <ArrowLeft size={20} /> Back to Series
                    </Link>
                </div>

                {/* Updated References Section */}
                {article.references && (
                    <div className="premium-footer-ref">
                        <h4 style={{ color: '#fff', marginBottom: '1rem' }}>References & Sources</h4>
                        <RichTextRenderer content={article.references} />
                    </div>
                )}
            </div>
            <Footer />
        </div>
    );
}

export default ShockTherapyArticle;
